import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import { isAxiosError } from "axios";
import { queue } from "@/components/Toast";
import { livePoll, useCalendarLive } from "./calendarLive";
import type { CalendarPlatform } from "@/types/contentCalendar.type";

function toastError(err: unknown, title: string) {
  const message = isAxiosError(err) ? (err.response?.data as { error?: string; message?: string } | undefined) : undefined;
  queue.add({ title, description: message?.error ?? message?.message ?? "Try again in a moment.", variant: "error" });
}

/** One audience comment on a live post, or a reply in its thread (yours or anyone's). */
export interface LiveComment {
  id: string;
  parentId: string | null;
  authorHandle: string;
  authorName: string | null;
  authorAvatar: string | null;
  /** Written by the brand's own account. */
  fromBrand: boolean;
  body: string;
  likeCount: number | null;
  postedAt: string;
  /** A reply sent from here. */
  sendStatus: "SENDING" | "SENT" | "FAILED" | null;
  sendError: string | null;
  sentByUserName: string | null;
  /** M7's line on what to do with it. */
  intent: string | null;
}

export interface LiveCommentThread extends LiveComment {
  replies: LiveComment[];
}

export interface LivePostComments {
  livePostId: string;
  platform: CalendarPlatform;
  /** The platform's own count, which can run past what was read. */
  commentCount: number | null;
  syncedAt: string | null;
  /** The platform's comments can be read from here at all (not TikTok). */
  readable: boolean;
  /** Why replying from here isn't possible now; null when it is. */
  replyBlock: string | null;
  threads: LiveCommentThread[];
}

export const liveCommentsKey = (slotId: string | null) => ["liveComments", slotId];

/** The audience's comments on a post's live posts, read with the numbers. */
export function useLiveComments(slotId: string | null, enabled = true) {
  const live = useCalendarLive();
  return useQuery({
    queryKey: liveCommentsKey(slotId),
    queryFn: async () => {
      const res = await axiosInstance.get<{ posts: LivePostComments[] }>(`/scheduling/slots/${slotId}/live-comments`);
      return res.data;
    },
    enabled: enabled && !!slotId,
    // A reply on its way is checked often; otherwise the next comments read is an hour off.
    refetchInterval: (query) =>
      livePoll(
        live,
        query.state.data?.posts.some((post) => post.threads.some((t) => t.replies.some((r) => r.sendStatus === "SENDING")))
          ? 3_000
          : 60_000,
      ),
    refetchOnWindowFocus: !live,
  });
}

function patchThread(
  data: { posts: LivePostComments[] } | undefined,
  livePostId: string,
  rootId: string,
  patch: (replies: LiveComment[]) => LiveComment[],
) {
  if (!data) return data;
  return {
    posts: data.posts.map((post) =>
      post.livePostId !== livePostId
        ? post
        : { ...post, threads: post.threads.map((t) => (t.id === rootId ? { ...t, replies: patch(t.replies) } : t)) },
    ),
  };
}

let pendingSeq = 0;

/**
 * Reply to an audience comment as the brand. The reply shows at once as
 * sending; the server's row replaces it (sent, or not sent with why), and a
 * refused request takes it back out.
 */
export function useReplyLiveComment(slotId: string | null) {
  const qc = useQueryClient();
  const key = liveCommentsKey(slotId);
  return useMutation({
    mutationFn: async (input: { livePostId: string; rootId: string; body: string; authorHandle: string; tempId: string }) => {
      const res = await axiosInstance.post<{ reply: LiveComment }>(
        `/scheduling/slots/${slotId}/live-posts/${input.livePostId}/comments/${input.rootId}/replies`,
        { body: input.body },
      );
      return res.data.reply;
    },
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: key });
      const pending: LiveComment = {
        id: input.tempId,
        parentId: input.rootId,
        authorHandle: input.authorHandle,
        authorName: null,
        authorAvatar: null,
        fromBrand: true,
        body: input.body,
        likeCount: null,
        postedAt: new Date().toISOString(),
        sendStatus: "SENDING",
        sendError: null,
        sentByUserName: null,
        intent: null,
      };
      qc.setQueryData<{ posts: LivePostComments[] }>(key, (data) =>
        patchThread(data, input.livePostId, input.rootId, (replies) => [...replies, pending]),
      );
    },
    onSuccess: (reply, input) => {
      qc.setQueryData<{ posts: LivePostComments[] }>(key, (data) =>
        patchThread(data, input.livePostId, input.rootId, (replies) => replies.map((r) => (r.id === input.tempId ? reply : r))),
      );
    },
    onError: (err, input) => {
      qc.setQueryData<{ posts: LivePostComments[] }>(key, (data) =>
        patchThread(data, input.livePostId, input.rootId, (replies) => replies.filter((r) => r.id !== input.tempId)),
      );
      toastError(err, "Could not send the reply");
    },
    onSettled: () => {
      if (qc.isMutating({ mutationKey: ["replyLiveComment", slotId] }) <= 1) void qc.invalidateQueries({ queryKey: key });
    },
    mutationKey: ["replyLiveComment", slotId],
  });
}

export function nextPendingReplyId(): string {
  pendingSeq += 1;
  return `pending-reply:${pendingSeq}`;
}

/** Send a reply that did not go out again; it shows as sending meanwhile. */
export function useRetryLiveReply(slotId: string | null) {
  const qc = useQueryClient();
  const key = liveCommentsKey(slotId);
  const setStatus = (input: { livePostId: string; rootId: string; replyId: string }, status: LiveComment["sendStatus"]) =>
    qc.setQueryData<{ posts: LivePostComments[] }>(key, (data) =>
      patchThread(data, input.livePostId, input.rootId, (replies) =>
        replies.map((r) => (r.id === input.replyId ? { ...r, sendStatus: status, sendError: null } : r)),
      ),
    );
  return useMutation({
    mutationFn: async (input: { livePostId: string; rootId: string; replyId: string }) => {
      const res = await axiosInstance.post<{ reply: LiveComment }>(
        `/scheduling/slots/${slotId}/live-posts/${input.livePostId}/comments/${input.replyId}/retry`,
      );
      return res.data.reply;
    },
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: key });
      setStatus(input, "SENDING");
    },
    onSuccess: (reply, input) => {
      qc.setQueryData<{ posts: LivePostComments[] }>(key, (data) =>
        patchThread(data, input.livePostId, input.rootId, (replies) => replies.map((r) => (r.id === input.replyId ? reply : r))),
      );
    },
    onError: (err, input) => {
      setStatus(input, "FAILED");
      toastError(err, "Could not send the reply");
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: key }),
  });
}
